import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Package,
  User,
  Calendar,
  Play,
  RotateCcw,
  UserPlus,
  ClipboardCheck,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/app/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { Label } from '@/app/components/ui/label';
import { Input } from '@/app/components/ui/input';
import { Textarea } from '@/app/components/ui/textarea';
import { toast } from '@/app/components/ui/simple-toaster';
import {
  useKit,
  useDeleteKit,
  useDeployKit,
  useReturnKit,
  useAssignTechToKit,
  useUnassignTechFromKit,
  useInspectKit,
} from '@/hooks/use-kits';
import { useOrgToday, useUsers } from '@/hooks/use-settings';
import { useLocations } from '@/hooks/use-locations';
import { PageBreadcrumb } from '@/app/components/page-breadcrumb';
import { formatDateOnly } from '@/lib/date-utils';
import type { KitStatus, KitAssetDetail, AssetStatus, InspectionResult } from '@/lib/types';
import { usePermissions } from '@/hooks/use-permissions';

const statusColors: Record<KitStatus, string> = {
  available: 'bg-green-100 text-green-800',
  deployed: 'bg-blue-100 text-blue-800',
  incomplete: 'bg-orange-100 text-orange-800',
};

const assetStatusColors: Record<AssetStatus, string> = {
  available: 'bg-green-100 text-green-800',
  deployed: 'bg-blue-100 text-blue-800',
  in_use: 'bg-blue-100 text-blue-800',
  retired: 'bg-gray-100 text-gray-800',
  damaged: 'bg-red-100 text-red-800',
  lost: 'bg-red-100 text-red-800',
  active: 'bg-green-100 text-green-800',
  quarantined: 'bg-red-100 text-red-800',
};

export function KitDetailPage() {
  const { canManageGear } = usePermissions();
  const orgToday = useOrgToday();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
    const kitId = parseInt(id || '0', 10);

  const { data: kit, isLoading, isError } = useKit(kitId);
  const { data: usersData } = useUsers({});
  const { data: locationsData } = useLocations({});

  const deleteMutation = useDeleteKit();
  const deployMutation = useDeployKit();
  const returnMutation = useReturnKit();
  const assignTechMutation = useAssignTechToKit();
  const unassignTechMutation = useUnassignTechFromKit();
  const inspectMutation = useInspectKit();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deployDialogOpen, setDeployDialogOpen] = useState(false);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [assignTechDialogOpen, setAssignTechDialogOpen] = useState(false);
  const [inspectDialogOpen, setInspectDialogOpen] = useState(false);

  const [deployLocationId, setDeployLocationId] = useState<string>('');
  const [deployUserId, setDeployUserId] = useState<string>('');
  const [deployDueDate, setDeployDueDate] = useState<string>('');
  const [deployNotes, setDeployNotes] = useState<string>('');
  const [returnNotes, setReturnNotes] = useState<string>('');
  const [selectedTechId, setSelectedTechId] = useState<string>('');
  const [inspectResult, setInspectResult] = useState<InspectionResult>('pass');
  const [inspectNotes, setInspectNotes] = useState<string>('');

  const users = usersData?.results || [];
  const locations = locationsData?.results || [];

    const handleDelete = async () => {
      try {
        await deleteMutation.mutateAsync(kitId);
        toast.success('Kit deleted successfully');
        navigate('/kits');
      } catch {
        toast.error('Failed to delete kit');
      }
    };

    const handleDeploy = async () => {
      if (!deployLocationId) {
        toast.error('Please select a location');
        return;
      }
      try {
        const result = await deployMutation.mutateAsync({
          id: kitId,
          data: {
            location_id: parseInt(deployLocationId, 10),
            assigned_to_user_id: deployUserId && deployUserId !== 'none' ? parseInt(deployUserId, 10) : undefined,
            due_date: deployDueDate || undefined,
            notes: deployNotes || undefined,
          },
        });
        toast.success(`Kit deployed: ${result.deployment_count} assets checked out`);
        setDeployDialogOpen(false);
        resetDeployForm();
      } catch {
        toast.error('Failed to deploy kit');
      }
    };

    const handleReturn = async () => {
      try {
        const result = await returnMutation.mutateAsync({
          id: kitId,
          data: { notes: returnNotes || undefined },
        });
        toast.success(`Kit returned: ${result.returned_count} assets checked in`);
        if (result.missing_assets.length > 0) {
          toast.error(`Warning: ${result.missing_assets.length} assets could not be returned`);
        }
        setReturnDialogOpen(false);
        setReturnNotes('');
      } catch {
        toast.error('Failed to return kit');
      }
    };

    const handleAssignTech = async () => {
      if (!selectedTechId) {
        toast.error('Please select a technician');
        return;
      }
      try {
        await assignTechMutation.mutateAsync({
          id: kitId,
          data: { user_id: parseInt(selectedTechId, 10) },
        });
        toast.success('Technician assigned successfully');
        setAssignTechDialogOpen(false);
        setSelectedTechId('');
      } catch {
        toast.error('Failed to assign technician');
      }
    };

    const handleUnassignTech = async () => {
      try {
        await unassignTechMutation.mutateAsync(kitId);
        toast.success('Technician unassigned successfully');
      } catch {
        toast.error('Failed to unassign technician');
      }
    };

    const handleInspect = async () => {
      try {
        const result = await inspectMutation.mutateAsync({
          id: kitId,
          data: {
            result: inspectResult,
            notes: inspectNotes || undefined,
          },
        });
        toast.success(`Inspection complete: ${result.inspection_count} assets inspected`);
        setInspectDialogOpen(false);
        setInspectResult('pass');
        setInspectNotes('');
      } catch {
        toast.error('Failed to start inspection');
      }
    };

  const resetDeployForm = () => {
    setDeployLocationId('');
    setDeployUserId('');
    setDeployDueDate('');
    setDeployNotes('');
  };

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError || !kit) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="text-center py-12">
          <AlertTriangle className="h-12 w-12 mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">Kit not found</h2>
          <p className="text-gray-600 mt-2">The kit you're looking for doesn't exist or you don't have access.</p>
          <Button asChild className="mt-4">
            <Link to="/kits">Back to Kits</Link>
          </Button>
        </div>
      </div>
    );
  }

  const isOverdue =
    kit.next_inspection_due && kit.next_inspection_due.slice(0, 10) < orgToday;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PageBreadcrumb items={[
        { label: 'Kits', href: '/kits' },
        { label: kit.name },
      ]} />
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/kits">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{kit.name}</h1>
          <p className="text-gray-600">{kit.description || 'No description'}</p>
        </div>
        <div className="flex gap-2">
          {canManageGear && (
            <Button variant="outline" asChild>
              <Link to={`/kits/${kit.id}/edit`}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Link>
            </Button>
          )}
          {canManageGear && (
            <Button variant="destructive" onClick={() => setDeleteDialogOpen(true)}>
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Package className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <Badge className={statusColors[kit.status]}>{kit.status}</Badge>
                {!kit.is_complete && (
                  <Badge variant="outline" className="ml-2 text-orange-600 border-orange-300">
                    Incomplete
                  </Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Package className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Assets</p>
                <p className="text-lg font-semibold">{kit.asset_count}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <User className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Assigned Tech</p>
                <p className="text-lg font-semibold">
                  {kit.assigned_to_tech_username || 'Unassigned'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Calendar className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Next Inspection</p>
                <p className={`text-lg font-semibold ${isOverdue ? 'text-red-600' : ''}`}>
                  {kit.next_inspection_due
                    ? formatDateOnly(kit.next_inspection_due)
                    : 'Not set'}
                </p>
                {isOverdue && <Badge variant="destructive">Overdue</Badge>}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {kit.status === 'available' && kit.can_deploy && (
              <Button onClick={() => setDeployDialogOpen(true)}>
                <Play className="h-4 w-4 mr-2" />
                Deploy Kit
              </Button>
            )}
            {kit.status === 'deployed' && (
              <Button onClick={() => setReturnDialogOpen(true)}>
                <RotateCcw className="h-4 w-4 mr-2" />
                Return Kit
              </Button>
            )}
            {kit.assigned_to_tech ? (
              <Button variant="outline" onClick={handleUnassignTech} disabled={unassignTechMutation.isPending}>
                <User className="h-4 w-4 mr-2" />
                Unassign Tech
              </Button>
            ) : (
              <Button variant="outline" onClick={() => setAssignTechDialogOpen(true)}>
                <UserPlus className="h-4 w-4 mr-2" />
                Assign Tech
              </Button>
            )}
            <Button variant="outline" onClick={() => setInspectDialogOpen(true)}>
              <ClipboardCheck className="h-4 w-4 mr-2" />
              Start Inspection
            </Button>
          </div>
          {!kit.can_deploy && kit.status === 'available' && (
            <p className="text-sm text-orange-600 mt-3">
              <AlertTriangle className="h-4 w-4 inline mr-1" />
              Cannot deploy: Some assets are not available for deployment.
            </p>
          )}
        </CardContent>
      </Card>

      {kit.missing_assets.length > 0 && (
        <Card className="border-orange-300">
          <CardHeader>
            <CardTitle className="text-orange-600">
              <AlertTriangle className="h-5 w-5 inline mr-2" />
              Missing Assets ({kit.missing_assets.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Serial Number</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {kit.missing_assets.map((asset) => (
                  <TableRow key={asset.id}>
                    <TableCell>
                      <Link
                        to={`/assets/${asset.id}`}
                        className="text-blue-600 hover:underline"
                      >
                        {asset.serial_number}
                      </Link>
                    </TableCell>
                    <TableCell>{asset.display_name}</TableCell>
                    <TableCell>
                      <Badge className={assetStatusColors[asset.status]}>{asset.status}</Badge>
                    </TableCell>
                    <TableCell className="text-gray-600">{asset.reason}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Kit Contents ({kit.asset_count} assets)</CardTitle>
        </CardHeader>
        <CardContent>
          {kit.assets_detail.length === 0 ? (
            <div className="text-center py-8">
              <Package className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">No assets in this kit.</p>
              {canManageGear && (
                <Button asChild className="mt-4">
                  <Link to={`/kits/${kit.id}/edit`}>Add Assets</Link>
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Serial Number</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Next Inspection</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {kit.assets_detail.map((asset) => (
                  <AssetRow key={asset.id} asset={asset} orgToday={orgToday} />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Kit</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{kit.name}"? This action cannot be undone.
              The assets in this kit will not be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Kit'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={deployDialogOpen} onOpenChange={setDeployDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deploy Kit</DialogTitle>
            <DialogDescription>
              Deploy all {kit.asset_count} assets in this kit to a location.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="deploy-location">Location *</Label>
              <Select value={deployLocationId} onValueChange={setDeployLocationId}>
                <SelectTrigger id="deploy-location">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((location) => (
                    <SelectItem key={location.id} value={String(location.id)}>
                      {location.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="deploy-user">Assign to User (optional)</Label>
              <Select value={deployUserId} onValueChange={setDeployUserId}>
                <SelectTrigger id="deploy-user">
                  <SelectValue placeholder="Select user" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={String(user.id)}>
                      {user.username}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="deploy-due-date">Due Date (optional)</Label>
              <Input
                id="deploy-due-date"
                type="date"
                value={deployDueDate}
                onChange={(e) => setDeployDueDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="deploy-notes">Notes (optional)</Label>
              <Textarea
                id="deploy-notes"
                value={deployNotes}
                onChange={(e) => setDeployNotes(e.target.value)}
                placeholder="Deployment notes..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeployDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleDeploy} disabled={deployMutation.isPending}>
              {deployMutation.isPending ? 'Deploying...' : 'Deploy Kit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={returnDialogOpen} onOpenChange={setReturnDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Return Kit</DialogTitle>
            <DialogDescription>
              Check in all deployed assets in this kit.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="return-notes">Notes (optional)</Label>
              <Textarea
                id="return-notes"
                value={returnNotes}
                onChange={(e) => setReturnNotes(e.target.value)}
                placeholder="Return notes..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReturnDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleReturn} disabled={returnMutation.isPending}>
              {returnMutation.isPending ? 'Returning...' : 'Return Kit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={assignTechDialogOpen} onOpenChange={setAssignTechDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Technician</DialogTitle>
            <DialogDescription>
              Assign this kit to a technician.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="assign-tech">Technician</Label>
              <Select value={selectedTechId} onValueChange={setSelectedTechId}>
                <SelectTrigger id="assign-tech">
                  <SelectValue placeholder="Select technician" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={String(user.id)}>
                      {user.username}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignTechDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAssignTech} disabled={assignTechMutation.isPending}>
              {assignTechMutation.isPending ? 'Assigning...' : 'Assign Tech'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={inspectDialogOpen} onOpenChange={setInspectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start Kit Inspection</DialogTitle>
            <DialogDescription>
              Create an inspection record for all {kit.asset_count} assets in this kit.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="inspect-result">Inspection Result</Label>
              <Select value={inspectResult} onValueChange={(v) => setInspectResult(v as InspectionResult)}>
                <SelectTrigger id="inspect-result">
                  <SelectValue placeholder="Select result" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pass">Pass</SelectItem>
                  <SelectItem value="fail">Fail</SelectItem>
                  <SelectItem value="conditional">Conditional</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="inspect-notes">Notes (optional)</Label>
              <Textarea
                id="inspect-notes"
                value={inspectNotes}
                onChange={(e) => setInspectNotes(e.target.value)}
                placeholder="Inspection notes..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInspectDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleInspect} disabled={inspectMutation.isPending}>
              {inspectMutation.isPending ? 'Creating...' : 'Create Inspections'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface AssetRowProps {
  asset: KitAssetDetail;
  orgToday: string;
}

function AssetRow({ asset, orgToday }: AssetRowProps) {
  const isOverdue =
    asset.next_inspection_due && asset.next_inspection_due.slice(0, 10) < orgToday;

  return (
    <TableRow>
      <TableCell>
        <Link
          to={`/assets/${asset.id}`}
          className="text-blue-600 hover:underline font-medium"
        >
          {asset.serial_number}
        </Link>
      </TableCell>
      <TableCell>{asset.display_name}</TableCell>
      <TableCell className="capitalize">{asset.type}</TableCell>
      <TableCell>
        <Badge className={assetStatusColors[asset.status]}>{asset.status}</Badge>
      </TableCell>
      <TableCell>
        {asset.next_inspection_due ? (
          <span className={isOverdue ? 'text-red-600' : ''}>
            {formatDateOnly(asset.next_inspection_due)}
            {isOverdue && <Badge variant="destructive" className="ml-2">Overdue</Badge>}
          </span>
        ) : (
          <span className="text-gray-400">Not set</span>
        )}
      </TableCell>
    </TableRow>
  );
}
